import type { FileContainerCollectionJSON } from './FileContainerCollection';
import { FileContainerCollection } from './FileContainerCollection';
import type { FileContainerAssetCopy } from './FileContainer';
import type { IPositionSetRepository, PositionSetJSON } from '@view-model/models/common/PositionSet';
import { PositionSet, PositionSetDeleteCommand, PositionSetUpdateCommand } from '@view-model/models/common/PositionSet';
import { PositionSetCreateCommand } from '@view-model/models/common/PositionSet/PositionSetCreateCommand';
import type { FileContainerId, ModelId, ViewModelAssetId, ViewModelId } from 'balus-common';
import { RTDBPath } from 'balus-common';
import type { Rect } from '@view-model/models/common/basic/Rect';
import type { ICommand } from '@model-framework/command';
import { CommandHelper, CompositeCommand } from '@model-framework/command';

export type FileContainerCollectionContentsJSON = {
  containers: FileContainerCollectionJSON;
  positions: PositionSetJSON;
};

export type LoadableFileContainerCollectionContentsJSON = Partial<FileContainerCollectionContentsJSON>;

export class FileContainerCollectionContents {
  constructor(
    public readonly containers: FileContainerCollection,
    public readonly positions: PositionSet
  ) {
    this.containers = containers.filter((container) => !!positions.find(container.id));
    this.positions = positions.subset(this.containers.allIds());
  }

  static buildEmpty(): FileContainerCollectionContents {
    return new FileContainerCollectionContents(FileContainerCollection.buildEmpty(), PositionSet.buildEmpty());
  }

  static load(dump: LoadableFileContainerCollectionContentsJSON): FileContainerCollectionContents {
    const { containers, positions } = dump;
    return new FileContainerCollectionContents(
      FileContainerCollection.load(containers || []),
      PositionSet.load(positions || {})
    );
  }

  dump(): FileContainerCollectionContentsJSON {
    return {
      containers: this.containers.dump(),
      positions: this.positions.dump(),
    };
  }

  isEqual(other: FileContainerCollectionContents): boolean {
    return this.containers.isEqual(other.containers) && this.positions.isEqual(other.positions);
  }

  isEmpty(): boolean {
    return this.containers.count() === 0;
  }

  count(): number {
    return this.containers.count();
  }

  allIds(): FileContainerId[] {
    return this.containers.allIds();
  }

  filterByIds(ids: FileContainerId[]): FileContainerCollectionContents {
    const containers = this.containers.filter((container) => ids.includes(container.id));
    return new FileContainerCollectionContents(containers, this.positions.subset(ids));
  }

  assetIds(): ViewModelAssetId[] {
    return this.containers.assetIds();
  }

  getRect(id: FileContainerId): Rect | undefined {
    const container = this.containers.get(id);
    const position = this.positions.find(id);
    if (!container || !position) return;

    return container.getRect(position);
  }

  cloneNew(
    assetCopy?: FileContainerAssetCopy
  ): [FileContainerCollectionContents, Record<FileContainerId, FileContainerId>] {
    const [containers, newIdMap] = this.containers.cloneNew(assetCopy);
    return [new FileContainerCollectionContents(containers, this.positions.cloneNew(newIdMap)), newIdMap];
  }

  move(dx: number, dy: number): FileContainerCollectionContents {
    return new FileContainerCollectionContents(this.containers, this.positions.moveAll(dx, dy));
  }

  filterByRectSelection(rect: Rect): FileContainerCollectionContents {
    return this.filterByIds(this.allIds().filter((id) => this.getRect(id)?.intersects(rect)));
  }

  buildCreateCommand(viewModelId: ViewModelId, modelId: ModelId): ICommand {
    return new CompositeCommand(
      this.containers.buildCreateCommand(viewModelId, modelId),
      new PositionSetCreateCommand(this.positions, RTDBPath.FileContainer.positionsPath(viewModelId, modelId))
    );
  }

  async buildDeleteCommand(
    viewModelId: ViewModelId,
    modelId: ModelId,
    positionSet: PositionSet
  ): Promise<ICommand | null> {
    if (this.isEmpty()) return null;

    const containerCommands = await Promise.all(
      this.allIds().map((id) =>
        CommandHelper.buildDeleteCommand(RTDBPath.FileContainer.containerPath(viewModelId, modelId, id))
      )
    );
    const positionsCommand = new PositionSetDeleteCommand(
      positionSet,
      RTDBPath.FileContainer.positionsPath(viewModelId, modelId)
    );

    return CompositeCommand.composeOptionalCommands(...containerCommands, positionsCommand);
  }

  buildMoveCommand(dx: number, dy: number, repository: IPositionSetRepository): ICommand | undefined {
    const { positions } = this;
    if (positions.isEmpty()) return;

    return new PositionSetUpdateCommand(positions, positions.moveAll(dx, dy), repository);
  }
}
